import React from 'react'
import { PreRegistrationForm } from '../components/PreRegistrationForm'
import {
  brandColors,
  typography,
  spacing,
  borderRadius,
  shadows,
  breakpoints,
} from '../theme'

export const LandingPage: React.FC = () => {
  return (
    <>
      <style>{`
        @media (max-width: ${breakpoints.tablet}px) {
          .landing-nav {
            flex-direction: column;
            gap: ${spacing[4]};
          }
          .landing-hero-grid {
            grid-template-columns: 1fr !important;
          }
          .landing-stats-grid {
            grid-template-columns: 1fr !important;
          }
          .landing-features-grid {
            grid-template-columns: 1fr !important;
          }
          .landing-pricing-grid {
            grid-template-columns: 1fr !important;
          }
        }
        html {
          scroll-behavior: smooth;
        }
        a[href^="#"] {
          transition: color 0.2s ease;
        }
        a[href^="#"]:hover {
          color: ${brandColors.primary};
        }
      `}</style>
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: brandColors.background,
          fontFamily: typography.fontFamily.primary,
        }}
      >
      {/* Navigation */}
      <nav
        style={{
          padding: `${spacing[4]} ${spacing[6]}`,
          backgroundColor: brandColors.surface,
          boxShadow: shadows.sm,
          position: 'sticky',
          top: 0,
          zIndex: 1000,
        }}
      >
        <div
          className="landing-nav"
          style={{
            maxWidth: breakpoints.wide,
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              fontSize: typography.fontSize.xl,
              fontWeight: typography.fontWeight.bold,
              color: brandColors.primary,
            }}
          >
            SovereignScope
          </div>
          <div style={{ display: 'flex', gap: spacing[4] }}>
            <a
              href="#features"
              style={{
                color: brandColors.textSecondary,
                textDecoration: 'none',
                fontSize: typography.fontSize.base,
              }}
            >
              Features
            </a>
            <a
              href="#pricing"
              style={{
                color: brandColors.textSecondary,
                textDecoration: 'none',
                fontSize: typography.fontSize.base,
              }}
            >
              Pricing
            </a>
            <a
              href="#contact"
              style={{
                color: brandColors.textSecondary,
                textDecoration: 'none',
                fontSize: typography.fontSize.base,
              }}
            >
              Contact
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        style={{
          padding: `${spacing[12]} ${spacing[4]}`,
          maxWidth: breakpoints.wide,
          margin: '0 auto',
        }}
      >
        <div
          className="landing-hero-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: spacing[8],
            alignItems: 'center',
          }}
        >
          <div>
            <h1
              style={{
                fontSize: 'clamp(2rem, 5vw, 3rem)',
                fontWeight: typography.fontWeight.bold,
                color: brandColors.textPrimary,
                lineHeight: typography.lineHeight.tight,
                marginBottom: spacing[6],
              }}
            >
              Turn 9-month procurement nightmares into{' '}
              <span style={{ color: brandColors.primary }}>4-week</span> data-driven
              decisions
            </h1>
            <p
              style={{
                fontSize: typography.fontSize.xl,
                color: brandColors.textSecondary,
                lineHeight: typography.lineHeight.relaxed,
                marginBottom: spacing[8],
              }}
            >
              Achieve <strong>30-50% cost savings</strong>, minimized regulatory risk,
              and a clear migration path towards digital sovereignty through our
              AI-powered platform.
            </p>
            <div
              style={{
                display: 'flex',
                gap: spacing[4],
                flexWrap: 'wrap',
              }}
            >
              <div
                style={{
                  padding: spacing[3],
                  backgroundColor: '#ECFDF5',
                  borderRadius: borderRadius.md,
                  border: '1px solid #10B981',
                }}
              >
                <div
                  style={{
                    fontSize: typography.fontSize['2xl'],
                    fontWeight: typography.fontWeight.bold,
                    color: '#059669',
                  }}
                >
                  30-50%
                </div>
                <div
                  style={{
                    fontSize: typography.fontSize.sm,
                    color: brandColors.textSecondary,
                  }}
                >
                  Cost Savings
                </div>
              </div>
              <div
                style={{
                  padding: spacing[3],
                  backgroundColor: '#EFF6FF',
                  borderRadius: borderRadius.md,
                  border: '1px solid #3B82F6',
                }}
              >
                <div
                  style={{
                    fontSize: typography.fontSize['2xl'],
                    fontWeight: typography.fontWeight.bold,
                    color: brandColors.primary,
                  }}
                >
                  4 weeks
                </div>
                <div
                  style={{
                    fontSize: typography.fontSize.sm,
                    color: brandColors.textSecondary,
                  }}
                >
                  vs 9 months
                </div>
              </div>
              <div
                style={{
                  padding: spacing[3],
                  backgroundColor: '#F5F3FF',
                  borderRadius: borderRadius.md,
                  border: '1px solid #8B5CF6',
                }}
              >
                <div
                  style={{
                    fontSize: typography.fontSize['2xl'],
                    fontWeight: typography.fontWeight.bold,
                    color: '#7C3AED',
                  }}
                >
                  AI-Powered
                </div>
                <div
                  style={{
                    fontSize: typography.fontSize.sm,
                    color: brandColors.textSecondary,
                  }}
                >
                  Assessment
                </div>
              </div>
            </div>
          </div>
          <div>
            <PreRegistrationForm variant="inline" />
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section
        id="problem"
        style={{
          padding: `${spacing[16]} ${spacing[6]}`,
          backgroundColor: brandColors.surface,
        }}
      >
        <div style={{ maxWidth: breakpoints.wide, margin: '0 auto' }}>
          <h2
            style={{
              fontSize: typography.fontSize['4xl'],
              fontWeight: typography.fontWeight.bold,
              color: brandColors.textPrimary,
              textAlign: 'center',
              marginBottom: spacing[4],
            }}
          >
            The Procurement Challenge
          </h2>
          <p
            style={{
              fontSize: typography.fontSize.lg,
              color: brandColors.textSecondary,
              textAlign: 'center',
              maxWidth: '800px',
              margin: '0 auto',
              marginBottom: spacing[12],
            }}
          >
            Traditional vendor evaluation processes take months, cost millions, and
            often fail to identify critical sovereignty risks until it's too late.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: spacing[4],
            }}
          >
            {[
              {
                title: '9+ Months',
                description: 'Average time for comprehensive vendor assessment',
                color: '#DC2626',
              },
              {
                title: '€500K+',
                description: 'Typical cost of manual due diligence processes',
                color: '#EA580C',
              },
              {
                title: '55%',
                description: 'Of M&A deals fail due to sovereignty concerns',
                color: '#D97706',
              },
            ].map((stat, idx) => (
              <div
                key={idx}
                style={{
                  padding: spacing[6],
                  backgroundColor: brandColors.background,
                  borderRadius: borderRadius.xl,
                  textAlign: 'center',
                  boxShadow: shadows.md,
                }}
              >
                <div
                  style={{
                    fontSize: typography.fontSize['3xl'],
                    fontWeight: typography.fontWeight.bold,
                    color: stat.color,
                    marginBottom: spacing[2],
                  }}
                >
                  {stat.title}
                </div>
                <div
                  style={{
                    fontSize: typography.fontSize.base,
                    color: brandColors.textSecondary,
                  }}
                >
                  {stat.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Overview */}
      <section
        id="features"
        style={{
          padding: `${spacing[16]} ${spacing[6]}`,
          backgroundColor: brandColors.background,
        }}
      >
        <div style={{ maxWidth: breakpoints.wide, margin: '0 auto' }}>
          <h2
            style={{
              fontSize: typography.fontSize['4xl'],
              fontWeight: typography.fontWeight.bold,
              color: brandColors.textPrimary,
              textAlign: 'center',
              marginBottom: spacing[4],
            }}
          >
            How SovereignScope Works
          </h2>
          <p
            style={{
              fontSize: typography.fontSize.lg,
              color: brandColors.textSecondary,
              textAlign: 'center',
              maxWidth: '800px',
              margin: '0 auto',
              marginBottom: spacing[12],
            }}
          >
            Our AI-powered platform automates digital sovereignty assessment across
            four critical dimensions
          </p>
          <div
            className="landing-features-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: spacing[6],
            }}
          >
            {[
              {
                title: 'Technology Stack Analysis',
                description:
                  'Automated SBOM processing, dependency mapping, and license classification with sovereignty risk scoring',
                icon: '🔧',
              },
              {
                title: 'Corporate Ownership Intelligence',
                description:
                  'Complete ownership structure analysis, jurisdiction mapping, and CLOUD Act exposure assessment',
                icon: '🏢',
              },
              {
                title: 'Data Residency & Hosting',
                description:
                  'Comprehensive data center mapping, GDPR compliance verification, and transfer mechanism analysis',
                icon: '🌍',
              },
              {
                title: 'EU Regulatory Compliance',
                description:
                  'Automated assessment of GDPR, AI Act, NIS2, and DSA compliance with gap analysis and remediation guidance',
                icon: '⚖️',
              },
            ].map((feature, idx) => (
              <div
                key={idx}
                style={{
                  padding: spacing[6],
                  backgroundColor: brandColors.surface,
                  borderRadius: borderRadius.xl,
                  boxShadow: shadows.md,
                }}
              >
                <div
                  style={{
                    fontSize: '3rem',
                    marginBottom: spacing[4],
                  }}
                >
                  {feature.icon}
                </div>
                <h3
                  style={{
                    fontSize: typography.fontSize.xl,
                    fontWeight: typography.fontWeight.semibold,
                    color: brandColors.textPrimary,
                    marginBottom: spacing[3],
                  }}
                >
                  {feature.title}
                </h3>
                <p
                  style={{
                    fontSize: typography.fontSize.base,
                    color: brandColors.textSecondary,
                    lineHeight: typography.lineHeight.relaxed,
                  }}
                >
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section
        style={{
          padding: `${spacing[16]} ${spacing[6]}`,
          backgroundColor: brandColors.surface,
        }}
      >
        <div style={{ maxWidth: breakpoints.wide, margin: '0 auto' }}>
          <h2
            style={{
              fontSize: typography.fontSize['4xl'],
              fontWeight: typography.fontWeight.bold,
              color: brandColors.textPrimary,
              textAlign: 'center',
              marginBottom: spacing[4],
            }}
          >
            Built for EU Procurement Teams
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: spacing[8],
              marginTop: spacing[12],
            }}
          >
            {[
              {
                persona: 'Procurement Directors',
                value:
                  'Reduce vendor evaluation time by 70% with automated sovereignty scoring and comparison tools',
              },
              {
                persona: 'VC Investment Teams',
                value:
                  'Identify sovereignty risks before investment with comprehensive due diligence automation',
              },
              {
                persona: 'M&A Practitioners',
                value:
                  'Quantify compliance costs and integration complexity with clear risk assessment',
              },
              {
                persona: 'CTOs & Tech Leaders',
                value:
                  'Maintain comprehensive visibility into organizational technology sovereignty posture',
              },
            ].map((item, idx) => (
              <div
                key={idx}
                style={{
                  padding: spacing[6],
                  backgroundColor: brandColors.background,
                  borderRadius: borderRadius.xl,
                  borderLeft: `4px solid ${brandColors.primary}`,
                }}
              >
                <h3
                  style={{
                    fontSize: typography.fontSize.xl,
                    fontWeight: typography.fontWeight.semibold,
                    color: brandColors.primary,
                    marginBottom: spacing[3],
                  }}
                >
                  {item.persona}
                </h3>
                <p
                  style={{
                    fontSize: typography.fontSize.base,
                    color: brandColors.textSecondary,
                    lineHeight: typography.lineHeight.relaxed,
                  }}
                >
                  {item.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section
        id="pricing"
        style={{
          padding: `${spacing[16]} ${spacing[6]}`,
          backgroundColor: brandColors.background,
        }}
      >
        <div style={{ maxWidth: breakpoints.wide, margin: '0 auto' }}>
          <h2
            style={{
              fontSize: typography.fontSize['4xl'],
              fontWeight: typography.fontWeight.bold,
              color: brandColors.textPrimary,
              textAlign: 'center',
              marginBottom: spacing[4],
            }}
          >
            Simple, Transparent Pricing
          </h2>
          <p
            style={{
              fontSize: typography.fontSize.lg,
              color: brandColors.textSecondary,
              textAlign: 'center',
              marginBottom: spacing[12],
            }}
          >
            Start with free public analysis, upgrade when you need private assessments
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: spacing[4],
            }}
          >
            {[
              { name: 'Free', price: '€0', description: 'Unlimited public analysis' },
              {
                name: 'Starter',
                price: '€399',
                description: '1 company, 3 documents',
              },
              {
                name: 'Enterprise',
                price: '€1,299',
                description: '5 companies, unlimited docs',
              },
              {
                name: 'Premium',
                price: '€2,999',
                description: 'Unlimited everything',
              },
            ].map((tier, idx) => (
              <div
                key={idx}
                style={{
                  padding: spacing[6],
                  backgroundColor: brandColors.surface,
                  borderRadius: borderRadius.xl,
                  boxShadow: shadows.md,
                  textAlign: 'center',
                  border:
                    tier.name === 'Enterprise'
                      ? `2px solid ${brandColors.primary}`
                      : 'none',
                }}
              >
                <h3
                  style={{
                    fontSize: typography.fontSize.xl,
                    fontWeight: typography.fontWeight.semibold,
                    color: brandColors.textPrimary,
                    marginBottom: spacing[2],
                  }}
                >
                  {tier.name}
                </h3>
                <div
                  style={{
                    fontSize: typography.fontSize['3xl'],
                    fontWeight: typography.fontWeight.bold,
                    color: brandColors.primary,
                    marginBottom: spacing[2],
                  }}
                >
                  {tier.price}
                  <span
                    style={{
                      fontSize: typography.fontSize.base,
                      fontWeight: typography.fontWeight.normal,
                      color: brandColors.textSecondary,
                    }}
                  >
                    /month
                  </span>
                </div>
                <p
                  style={{
                    fontSize: typography.fontSize.sm,
                    color: brandColors.textSecondary,
                  }}
                >
                  {tier.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Compliance */}
      <section
        style={{
          padding: `${spacing[16]} ${spacing[6]}`,
          backgroundColor: brandColors.surface,
        }}
      >
        <div style={{ maxWidth: breakpoints.wide, margin: '0 auto' }}>
          <h2
            style={{
              fontSize: typography.fontSize['4xl'],
              fontWeight: typography.fontWeight.bold,
              color: brandColors.textPrimary,
              textAlign: 'center',
              marginBottom: spacing[4],
            }}
          >
            EU-Sovereign by Design
          </h2>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: spacing[8],
              flexWrap: 'wrap',
            }}
          >
            {[
              'GDPR Compliant',
              'EU-Only Data Centers',
              'ISO 27001 Certified',
              'SOC 2 Type II',
            ].map((badge, idx) => (
              <div
                key={idx}
                style={{
                  padding: `${spacing[3]} ${spacing[6]}`,
                  backgroundColor: brandColors.background,
                  borderRadius: borderRadius.full,
                  border: `1px solid ${brandColors.primary}`,
                  color: brandColors.primary,
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.medium,
                }}
              >
                {badge}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        id="contact"
        style={{
          padding: `${spacing[20]} ${spacing[6]}`,
          backgroundColor: brandColors.primary,
          color: 'white',
        }}
      >
        <div style={{ maxWidth: breakpoints.wide, margin: '0 auto', textAlign: 'center' }}>
          <h2
            style={{
              fontSize: typography.fontSize['4xl'],
              fontWeight: typography.fontWeight.bold,
              marginBottom: spacing[4],
            }}
          >
            Ready to Transform Your Procurement Process?
          </h2>
          <p
            style={{
              fontSize: typography.fontSize.xl,
              marginBottom: spacing[8],
              opacity: 0.9,
            }}
          >
            Join the waitlist to be among the first to experience SovereignScope
          </p>
          <div
            style={{
              maxWidth: '600px',
              margin: '0 auto',
            }}
          >
            <PreRegistrationForm variant="inline" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        style={{
          padding: `${spacing[8]} ${spacing[6]}`,
          backgroundColor: brandColors.textPrimary,
          color: 'white',
        }}
      >
        <div
          style={{
            maxWidth: breakpoints.wide,
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
            }}
          >
            SovereignScope
          </div>
          <div
            style={{
              fontSize: typography.fontSize.sm,
              opacity: 0.8,
            }}
          >
            © 2025 SovereignScope. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
    </>
  )
}

